import React from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToastDefault } from "@/hooks/use-toast-default";
import { useReactHookFormAxiosValidation } from "@/hooks/use-react-hook-form-axios-validation";
import { FormInput } from "@/components/forms/input";
import { FormSelect } from "@/components/ui/select";
import { useGetSnapshotCollectionsQuery } from "../../snapshotCollection.api";
import { BusinessReportOutcome } from "@/modules/snapshots/businessReports.types";
import { useUpdateBusinessReportOutcomeMutation } from "../../businessReports.api";
import {
  getSnapshotCollectionOptions,
  validateSnapshotCollection,
} from "@/modules/snapshots/modals/generateSnapshotCollectionModal/helpers";

interface Props {
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  businessReportOutcome: BusinessReportOutcome;
}

const CREATE_NEW_COLLECTION_KEY = "create-new-collection";

interface FormInputs {
  name: string;
  snapshot_collection_id: string;
  snapshot_collection_new: string;
}

export const SaveToSnapshotCollectionModal: React.FC<Props> = ({ setIsOpen, isOpen, businessReportOutcome }) => {
  const { showToast } = useToastDefault();
  const [updateBusinessReportOutcome] = useUpdateBusinessReportOutcomeMutation();

  const methods = useForm<FormInputs>({
    defaultValues: {
      name: businessReportOutcome.fs_entry?.document_name || "",
    },
  });

  const { data: snapshotCollections = [] } = useGetSnapshotCollectionsQuery({
    business_report_id: businessReportOutcome.business_report_id,
  });

  const handleReactHookFormAxiosValidation = useReactHookFormAxiosValidation<FormInputs>();

  const snapshotCollectionsOptions = React.useMemo(
    () => getSnapshotCollectionOptions(snapshotCollections),
    [snapshotCollections],
  );

  const selectedSnapshotCollection = useWatch({
    control: methods.control,
    name: "snapshot_collection_id",
  });

  React.useEffect(() => {
    if (selectedSnapshotCollection !== CREATE_NEW_COLLECTION_KEY) {
      methods.setValue("snapshot_collection_new", "");
    }
  }, [methods, selectedSnapshotCollection]);

  const onSubmit = async (payload: FormInputs) => {
    if (!validateSnapshotCollection(selectedSnapshotCollection, payload, methods.setError)) {
      return;
    }

    try {
      await updateBusinessReportOutcome({
        id: businessReportOutcome.id,
        data: {
          snapshot_collection_id:
            payload.snapshot_collection_id === CREATE_NEW_COLLECTION_KEY ? undefined : payload.snapshot_collection_id,
          snapshot_collection_new: payload.snapshot_collection_new,
          name: payload.name,
        },
      }).unwrap();

      setIsOpen(false);
      showToast("Success", "SnapShot updated");
    } catch (error) {
      handleReactHookFormAxiosValidation(error, methods.setError);
    }
  };

  return (
    <FormProvider {...methods}>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <form method="post" onSubmit={methods.handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>Saving Snapshot to Collection</DialogTitle>
            </DialogHeader>
            <FormInput autoFocus={false} name="name" placeholder="SnapShot name" className="my-3" />

            <div className="my-3">
              <FormSelect
                name="snapshot_collection_id"
                placeholder="Select snapshot collection"
                options={snapshotCollectionsOptions}
                required={true}
              />
            </div>

            {selectedSnapshotCollection === CREATE_NEW_COLLECTION_KEY && (
              <FormInput name="snapshot_collection_new" placeholder="New snapshot collection name" className="my-3" />
            )}

            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={methods.formState.isSubmitting}>
                {methods.formState.isSubmitting ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </FormProvider>
  );
};
